import React, { useEffect, useState } from "react";
import ShareSpaceMessageBlock from "./share-space-message-block";
import ShareSpaceItemDisplayList from "./share-space-item-display-list";
import { useHistory, useParams } from "react-router";
import ShareSpaceManifestService from "utilities/services/share-space/recipient-access/share-space-manifest-service";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import ShareSpaceManifestRecord from "models/view-models/share-spaces/share-space-manifest-record";
import Loader from "atoms/loaders/loader";
import { LoaderStyles } from "atoms/constants/loader-styles";
import moment from "moment";
import { siteMap } from "internal-sitemap";
import RecipientAccessShareSpaceRecipientService from "utilities/services/share-space/recipient-access/share-space-recipient-service";

interface ShareSpaceRecipientPageProps {}

interface ShareSpaceRecipientParams {
    guid: string;
}

const BASE_CLASS = "c-share-space-recipient-page";

const ShareSpaceRecipientPage: React.FC<ShareSpaceRecipientPageProps> = () => {
    const { guid } = useParams<ShareSpaceRecipientParams>();
    const [isLoading, setIsLoading] = useState(false);
    const [manifest, setManifest] = useState<ShareSpaceManifestRecord>();
    const history = useHistory();
    const { patch: trackAccess } =
        RecipientAccessShareSpaceRecipientService.usePatchTrackAccess();

    useEffect(() => {
        const getManifest = async () => {
            setIsLoading(true);
            try {
                const manifestResponse = await ShareSpaceManifestService.get({
                    guid,
                });
                if (
                    !manifestResponse.resultObject ||
                    manifestResponse.result?.hasErrors()
                ) {
                    throw new Error();
                }
                setManifest(manifestResponse.resultObject);
            } catch (error) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("loading"),
                        resource: t("information").toLowerCase(),
                    })
                );
            } finally {
                setIsLoading(false);
            }
        };
        getManifest();
    }, [guid]);

    // Track access without caring if the request fails
    useEffect(() => {
        trackAccess({ guid });
    }, [guid, trackAccess]);

    // Redirect if expired
    useEffect(() => {
        if (manifest?.expiresOn && moment().isAfter(manifest?.expiresOn)) {
            history.push(siteMap.errors.notFound);
        }
    }, [history, manifest?.expiresOn]);

    return (
        <section className={BASE_CLASS}>
            <div className={`${BASE_CLASS}__shareMessageWrapper`}>
                {isLoading ? (
                    <Loader type={LoaderStyles.LinkGlyph} />
                ) : (
                    <ShareSpaceMessageBlock
                        guid={guid}
                        fullName={`${manifest?.senderFirstName} ${manifest?.senderLastName}`}
                        message={manifest?.description}
                        expiresOn={manifest?.expiresOn}
                        sentOn={manifest?.sentOn}
                        replyToEmailAddress={manifest?.senderEmail}
                    />
                )}
            </div>
            <div className={`${BASE_CLASS}__shareItemsWrapper`}>
                <ShareSpaceItemDisplayList guid={guid} />
            </div>
        </section>
    );
};

export default ShareSpaceRecipientPage;
